import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { cloneDeep } from "lodash";
import clsx from "clsx";

import useLanguageChange from "../../../hooks/useLanguageChange";

import { GLOBAL_MEETING_DATA, PublicVariables } from "../../../constants/PublicVariables";
import {
    FROM_WHERE,
    IMAGE_OBJ_KEYS,
    MAX_ALLOWED_FILE_IN_SINGLE_TIME,
    MAX_IMAGE_COUNT_OF_INDEXED_DB,
    PICKED_FILES_FROM,
    SELECTED_SCREEN,
    SUB_SCREEN,
    UPLOADING_FILE_STATUS,
    UPLOAD_FILES_DATA,
    UPLOAD_FILE_MARKUP_CAPTION_MAX_LENGTH,
} from "../../../constants/UploadFilesConstants";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY, FIREBASE_LOG_VALUE } from "../../../constants/FirebaseConstants";

import { adjustTextareaHeight, isEnterpriseEdition, replaceAtIndex, spliceAtIndex } from "../../../shared/utility";
import {
    changeIsFromMarkupValue,
    changePreviousScreen,
    changeUploadFileScreen,
    changeUploadFileSubScreen,
    setIsHideFinalAddBtn,
    updateImagesArray,
} from "../../../reducers/uploadFile-reducer";

import CancelUploadModal from "../CancelUploadModal";
import uploadFilesService from "../../../services/uploadFilesService";
import { LogFirebase } from "../../../services/firestore";
import googleEvent from "../../../services/googleEvent";
import { EVENT_NAME } from "../../../constants/GTagConstants";
import MarkupMenubar from "../../Markup/MarkupMenubar";
import UploadFileMarkupService from "../../../services/UploadFileMarkupService/UploadFileMarkupService";
import { SHAPE_TYPE } from "../../../constants/Constants";
import { drawOnCanvas } from "../../Markup/MarkupFunction";
import {
    setIsShadowOrStroke,
    setSelectedColorIndex,
    setSelectedFontSizeForShape,
    setSelectedThicknessForShape,
} from "../../../reducers/markup-reducer";
import { MARKUP_MENU_TYPE } from "../../../constants/MarkupConstants";
import { indexedDBService } from "../../OfflineMode/indexedDBService";

const MarkupFiles = ({ updatedSelectedFiles }) => {
    const dispatch = useDispatch();
    const prevMarkupCountRef = useRef(0);

    const { previousScreen, imagesArray, screenToShow, subScreenToShow, isFromMarkup } = useSelector((state) => state.uploadFile);
    const { selectedShapeType } = useSelector((state) => state.markup);
    const { isOfflineModeActive } = useSelector((state) => state.offlineMode);

    const [showCancelUploadModal, setShowCancelUploadModal] = useState(false);
    const [showAddButton, setShowAddButton] = useState(true);
    const [caption, setCaption] = useState("");
    const [sliderButtons, setSliderButtons] = useState({
        showButtons: false,
        showNextButton: false,
        showPreviousButton: false,
    });
    const [disablePlusButton, setDisablePlusButton] = useState(false);
    const uploadFileMarkupRef = useRef();

    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change*/
    const localisedValues = useLanguageChange();

    useEffect(() => {
        uploadFileMarkupRef.current?.classList?.add("active");
        return () => {
            uploadFileMarkupRef.current?.classList?.remove("active");
        };
    }, []);

    useEffect(() => {
        if (screenToShow === SELECTED_SCREEN.MARKUP && subScreenToShow === SUB_SCREEN.OFF_VIEW && imagesArray?.length > 0) {
            const isAllowAddImage = isAllowAddImageButtonFromMarkup();
            setShowAddButton(isAllowAddImage);
            if (!isAllowAddImage) {
                dispatch(setIsHideFinalAddBtn(true));
            }
            setMarkupScreen();
        }
    }, [screenToShow, subScreenToShow]);

    useEffect(() => {
        if (!selectedShapeType) {
            showHideSliderButtons(UploadFileMarkupService.getSelectedFileIndex());
        } else {
            showHideSliderButtons(-1);
        }
    }, [selectedShapeType, screenToShow, subScreenToShow]);

    const setMarkupScreen = () => {
        if (previousScreen !== SUB_SCREEN.CAMERA_VIEW) {
            /* perform camera actions */
            LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.FileList, UPLOAD_FILES_DATA.logFileArray);
        }
        const numberOfMarkupImages = getImageCountByStatus(); // get markup images count
        let loadingFirstTime = false;
        if (UPLOAD_FILES_DATA.selectedImageObj === null) {
            loadingFirstTime = true;
            selectImage(imagesArray[imagesArray.length - 1], loadingFirstTime);
        } else if (
            UPLOAD_FILES_DATA.pickedFilesFrom !== PICKED_FILES_FROM.NONE &&
            (PublicVariables.isMobile || prevMarkupCountRef.current !== numberOfMarkupImages)
        ) {
            selectImage(imagesArray[imagesArray.length - 1], loadingFirstTime);
        } else {
            showHideSliderButtons(UPLOAD_FILES_DATA.selectedImageIndex);
        }

        prevMarkupCountRef.current = numberOfMarkupImages; // update markup images count ref
        /* 
            - initialize color pallate for markup
            - loadingFirstTime variable will be used here for initializinf color pallate
        */
        UPLOAD_FILES_DATA.logFileArray = [];
    };

    const generateImageObjectWithShapes = (imgObj) => {
        imgObj.shapeList?.forEach((shape, index) => {
            let formattedShape = shape;
            if (typeof shape === "string") {
                formattedShape = UploadFileMarkupService.generateShapeFromObject(JSON.parse(shape));
            }

            imgObj.shapeList[index] = formattedShape;
        });

        imgObj.undoList?.forEach((shape, index) => {
            let formattedShape = shape;
            if (typeof shape === "string") {
                formattedShape = UploadFileMarkupService.generateShapeFromObject(JSON.parse(shape));
            }

            imgObj.undoList[index] = formattedShape;
        });

        return imgObj;
    };

    const selectImage = async (newImageObj, loadingFirstTime = false, updatedImageArrayIndex = -1) => {
        if (selectedShapeType === SHAPE_TYPE.TEXT) {
            UploadFileMarkupService.checkMarkupTextStatus();
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        UploadFileMarkupService.handleShapeCount(0, true);
        const imageIndex = updatedImageArrayIndex < 0 ? imagesArray.findIndex((image) => image.id === newImageObj.id) : updatedImageArrayIndex;
        if (!selectedShapeType) {
            showHideSliderButtons(UploadFileMarkupService.getSelectedFileIndex());
        } else {
            showHideSliderButtons(-1);
        }
        UploadFileMarkupService.checkMarkupTextStatus();
        if (UPLOAD_FILES_DATA.selectedImageObj !== null && UPLOAD_FILES_DATA.selectedImageIndex >= 0) {
            UPLOAD_FILES_DATA.selectedImageObj = {
                ...UPLOAD_FILES_DATA.selectedImageObj,
                caption: caption,
            };
            const updatedImageArray = replaceAtIndex(imagesArray, UPLOAD_FILES_DATA.selectedImageIndex, UPLOAD_FILES_DATA.selectedImageObj);
            dispatch(updateImagesArray(updatedImageArray));
        }

        UploadFileMarkupService.markupVar.shape = null; // don't select anything in the mark up
        UPLOAD_FILES_DATA.selectedImageObj = generateImageObjectWithShapes(cloneDeep(newImageObj));
        UPLOAD_FILES_DATA.selectedImageIndex = imageIndex;
        setCaption(UPLOAD_FILES_DATA.selectedImageObj.caption);
        LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.Markup, { selected_markup_file_name: newImageObj.name });

        const selectedColorIndex = newImageObj.anncolorIndex;
        document.querySelector("input[name='colorpickerFile'][value='" + selectedColorIndex + "']").checked = true;
        const fillClassFileId = newImageObj.anncolorIndex; //as per array index 0 to...
        dispatch(setSelectedColorIndex(fillClassFileId));

        const isShadowOrStorke = newImageObj.isShadowOrStorke;
        document.querySelector("input[id='colorpickerStrokeCheckbox']").checked = true;
        dispatch(setIsShadowOrStroke(isShadowOrStorke));

        const selectedFontSizeForImage = newImageObj.selectedFontSize;
        const selectedThicknessForImage = newImageObj.selectedThickness;

        dispatch(setSelectedThicknessForShape(selectedThicknessForImage));
        dispatch(setSelectedFontSizeForShape(selectedFontSizeForImage));

        const annCanvas = UPLOAD_FILES_DATA.annCanvas;
        if (annCanvas) {
            annCanvas.isDrawingMode = false;
            annCanvas.clear();
            annCanvas.discardActiveObject();
        }

        UploadFileMarkupService.markupVar.clearMarkup = false;

        drawOnCanvas(loadingFirstTime, UPLOAD_FILES_DATA.selectedImageObj);
        // Set default pencil selected when image select
        if (!UploadFileMarkupService.firstTime) {
            UploadFileMarkupService.selectShape(SHAPE_TYPE.FREEDRAW);
        }
    };

    const closeButtonClickHandler = () => {
        setShowCancelUploadModal(true);
    };

    const onCloseCancelUploadModal = () => {
        setShowCancelUploadModal(false);
    };

    const onConfirmCancelMarkup = () => {
        setShowCancelUploadModal(false);

        let filteredImagesArray = [];
        if (previousScreen === SELECTED_SCREEN.FINAL_UPLODING_VIEW) {
            filteredImagesArray = imagesArray.filter((image) => image[IMAGE_OBJ_KEYS.UPLOADING_STATUS] !== UPLOADING_FILE_STATUS.MARKUP);
        }

        dispatch(changeUploadFileScreen(previousScreen));
        dispatch(updateImagesArray(filteredImagesArray));

        setShowAddButton(true);
        /* reset selected image values */
        UPLOAD_FILES_DATA.selectedImageIndex = -1;
        UPLOAD_FILES_DATA.selectedImageObj = null;

        googleEvent.logEvent(EVENT_NAME.UPLOAD_FILE_CANCEL_FROM_MARKUP_TAPPED);
        if (UPLOAD_FILES_DATA.annCanvas) {
            UPLOAD_FILES_DATA.annCanvas.dispose();
            UPLOAD_FILES_DATA.annCanvas = false;
            UploadFileMarkupService.firstTime = true;
        }
    };

    const addImageClickHandler = () => {
        if (!isFromMarkup) {
            googleEvent.logEvent(EVENT_NAME.UPLOAD_FILE_FROM_MARKUP_TAPPED);
        }

        let fromMarkup = false;
        let type = "";
        if (UPLOAD_FILES_DATA.pickedFilesFrom === PICKED_FILES_FROM.FROM_CAMERA) {
            if (PublicVariables.isMobile) {
                fromMarkup = true;
                document.getElementById("cameraFileInput_MarkupFiles")?.click();
            } else {
                dispatch(changeUploadFileSubScreen(SUB_SCREEN.CAMERA_VIEW));
                if (previousScreen === SELECTED_SCREEN.FINAL_UPLODING_VIEW) {
                    dispatch(changePreviousScreen(SELECTED_SCREEN.MARKUP_WITH_DONE));
                }
            }
        } else if (UPLOAD_FILES_DATA.pickedFilesFrom === PICKED_FILES_FROM.FROM_GALLERY) {
            fromMarkup = true;
            document.getElementById("file-upload-from-markup")?.click();
            type = FROM_WHERE.GALLERY;
        }

        dispatch(changeIsFromMarkupValue(fromMarkup));
        LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.Markup, FIREBASE_LOG_VALUE.PlusClickWithType + type);
    };

    const uploadClickHandler = () => {
        let updatedImageArray = cloneDeep(imagesArray);

        if (UPLOAD_FILES_DATA.selectedImageObj !== null) {
            const oldFileObjIndex = updatedImageArray.findIndex((image) => image.id === UPLOAD_FILES_DATA.selectedImageObj.id);
            UPLOAD_FILES_DATA.selectedImageObj = {
                ...generateImageObjectWithShapes(cloneDeep(UPLOAD_FILES_DATA.selectedImageObj)),
                caption: caption,
            };
            updatedImageArray = replaceAtIndex(updatedImageArray, oldFileObjIndex, cloneDeep(UPLOAD_FILES_DATA.selectedImageObj));
        }

        updatedImageArray.map((img) => {
            if (img[IMAGE_OBJ_KEYS.UPLOADING_STATUS] === UPLOADING_FILE_STATUS.MARKUP) {
                img[IMAGE_OBJ_KEYS.UPLOADING_STATUS] = UPLOADING_FILE_STATUS.READY_TO_UPLOAD;
            }

            return img;
        });

        if (UPLOAD_FILES_DATA.annCanvas) {
            UPLOAD_FILES_DATA.annCanvas.dispose();
            UPLOAD_FILES_DATA.annCanvas = false;
            UploadFileMarkupService.firstTime = true;
        }

        dispatch(updateImagesArray(updatedImageArray));
        dispatch(changeUploadFileScreen(SELECTED_SCREEN.FINAL_UPLODING_VIEW));

        googleEvent.logEvent(EVENT_NAME.UPLOAD_TAPPED);
        LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.Markup, FIREBASE_LOG_VALUE.UploadFileClickedOnMarkup);
    };

    const preventEvent = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const dragChange = (e) => {
        preventEvent(e);
        const allFiles = Array.from(e.target.files);
        dispatch(changeIsFromMarkupValue(false));
        if (allFiles.length > 0) uploadFilesService.generateFilesArray(allFiles, updatedSelectedFiles);
    };

    const imageNavigationButtonClickHandler = (navigationValue) => {
        const updatedImageIndex = UPLOAD_FILES_DATA.selectedImageIndex + navigationValue;
        if (updatedImageIndex >= 0 && updatedImageIndex < getImageCountByStatus()) {
            selectImage(imagesArray[updatedImageIndex], false, updatedImageIndex);
        }
    };

    const isAllowAddImageButtonFromMarkup = () => {
        if (isEnterpriseEdition()) {
            return getImageCountByStatus() < MAX_ALLOWED_FILE_IN_SINGLE_TIME;
        } else {
            return getImageCountByStatus() < GLOBAL_MEETING_DATA.remainingFileCount;
        }
    };

    const captionChangeHandler = (e) => {
        const val = e.target.value?.substring(0, UPLOAD_FILE_MARKUP_CAPTION_MAX_LENGTH);
        setCaption(val);
    };

    /* 
    Get number of images exist in imagesArray by its current status
    with default status to get count is Markup 
    */
    const getImageCountByStatus = (fileStatus = UPLOADING_FILE_STATUS.MARKUP, updatedImageArray = null) => {
        return (updatedImageArray ?? imagesArray).filter((image) => image[IMAGE_OBJ_KEYS.UPLOADING_STATUS] === fileStatus).length;
    };

    const updateImageSliderButtonValue = (showButtonsDiv, showNextButtonDiv, ShowPreviousButtonDiv) => {
        setSliderButtons({
            showButtons: showButtonsDiv,
            showNextButton: showNextButtonDiv,
            showPreviousButton: ShowPreviousButtonDiv,
        });
    };

    const resetImageSliderButtons = () => {
        updateImageSliderButtonValue(false, false, false);
    };

    /* send -1 if markup tool is selected */
    const showHideSliderButtons = (imageIndex) => {
        // old fn name enableDisableImageSliderButtons
        resetImageSliderButtons(); //Reset slider buttons before checking based on selected image

        /*
        update buttons visibility only if sent parameter value is greater than -1 as we are using -1 if selected image has markup tool selected
        And only if images are more than 1 as no slider button will require if only one image is there
        */
        const numberOfMarkupImages = getImageCountByStatus();
        if (imageIndex >= 0 && numberOfMarkupImages > 1) {
            if (imageIndex === imagesArray.findIndex((image) => image[IMAGE_OBJ_KEYS.UPLOADING_STATUS] === UPLOADING_FILE_STATUS.MARKUP)) {
                updateImageSliderButtonValue(true, true, false); //As this is first image, hide the previous slider button
            } else if (imageIndex === imagesArray.length - 1) {
                updateImageSliderButtonValue(true, false, true); //As this is last image, hide the next slider button
            } else {
                updateImageSliderButtonValue(true, true, true); //As image is neighter first image nor last image, both slider buttons will be visible
            }
        }
    };

    const adjustCaptionFieldHeight = (e) => {
        adjustTextareaHeight(e.target);
    };

    const deleteImageFromMarkUp = (index) => {
        googleEvent.logEvent(EVENT_NAME.UPLOAD_FILE_DELETE_FROM_MARKUP_TAPPED);

        let imageCount = getImageCountByStatus();

        if (imageCount == 1) {
            /* While deleting image from markup screen, check if only one image exist in bottom bar or not */
            /* Show close markup confirmation modal */
            closeButtonClickHandler();
            return;
        }

        const splicedImagesArray = spliceAtIndex(imagesArray, index);

        imageCount = getImageCountByStatus(UPLOADING_FILE_STATUS.MARKUP, splicedImagesArray);
        const isSelectedImageObjToDelete = UPLOAD_FILES_DATA.selectedImageObj?.id === imagesArray[index]?.id;
        if (isSelectedImageObjToDelete) {
            const newIndex = index - (imageCount > index ? 0 : 1);
            selectImage(splicedImagesArray[newIndex], false, newIndex);
        } else {
            if (UPLOAD_FILES_DATA.selectedImageIndex > index) {
                UPLOAD_FILES_DATA.selectedImageIndex--;
            }
            showHideSliderButtons(UPLOAD_FILES_DATA.selectedImageIndex);
        }

        dispatch(updateImagesArray(splicedImagesArray));
        setShowAddButton(true); //Show Add button as one image is removed from limitation
    };

    useEffect(() => {
        if (isOfflineModeActive) {
            indexedDBService.getAllImages().then((storedSessionImages) => {
                // Count how many images are in MARKUP status
                const markupImageCount = imagesArray.filter((img) => img.uploadingStatus === UPLOADING_FILE_STATUS.MARKUP).length;
                const totalImageCount = markupImageCount + storedSessionImages?.length;
                if (totalImageCount >= MAX_IMAGE_COUNT_OF_INDEXED_DB) {
                    LogFirebase("OfflineMode", "MaxImageCountReached", {
                        count: totalImageCount,
                    });
                    setDisablePlusButton(true);
                } else {
                    LogFirebase("OfflineMode", "MaxImageCountNotReached", {
                        count: totalImageCount,
                    });
                    setDisablePlusButton(false);
                }
            });
        }
    }, [imagesArray, isOfflineModeActive]);

    return (
        <>
            <div ref={uploadFileMarkupRef} className='upload-file-markup' data-testid='uploadFileMarkup'>
                <div className='upload-file-markup-header'>
                    <div className='upload-file-markup-close custom-tooltip tooltip-auto-width'>
                        <button className='btn close-btn' onClick={() => closeButtonClickHandler()}>
                            <span className='btn-icon icon-close-light'></span>
                        </button>
                        <div className='custom-tooltip-content text-nowrap'>
                            <div className='custom-tooltip-in'>
                                <p>{localisedValues?.cancel}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='upload-file-markup-body'>
                    {sliderButtons.showButtons && (
                        <div className='file-markup-navigation'>
                            {sliderButtons.showPreviousButton && (
                                <div className='navigation-button left-navigation custom-tooltip right-tooltip tooltip-auto-width m-hide'>
                                    <button className='btn' onClick={() => imageNavigationButtonClickHandler(-1)}>
                                        <span className='btn-icon icon-previous'></span>
                                    </button>
                                    <div className='custom-tooltip-content text-nowrap'>
                                        <div className='custom-tooltip-in'>
                                            <p>{localisedValues?.previous}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {sliderButtons.showNextButton && (
                                <div className='navigation-button right-navigation custom-tooltip left-tooltip tooltip-auto-width m-hide'>
                                    <button className='btn' onClick={() => imageNavigationButtonClickHandler(1)}>
                                        <span className='btn-icon icon-next'></span>
                                    </button>
                                    <div className='custom-tooltip-content text-nowrap'>
                                        <div className='custom-tooltip-in'>
                                            <p>{localisedValues?.next_lable}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className='upload-file-markup-view'>
                        <div className='upload-file-markup-box'>
                            <canvas style={{ display: "block" }} id='multiMarkupCanvas'></canvas>
                        </div>
                    </div>
                    <div className='upload-file-caption'>
                        <div className='caption-frm'>
                            <div className='caption-form-group'>
                                <div className='inputchar-count'>{UPLOAD_FILE_MARKUP_CAPTION_MAX_LENGTH - caption.length}</div>
                                <div className='caption-form-group-in'>
                                    <textarea
                                        className='caption-textarea default-custom-scroll'
                                        maxLength={UPLOAD_FILE_MARKUP_CAPTION_MAX_LENGTH}
                                        placeholder={localisedValues?.add_caption}
                                        onChange={(e) => captionChangeHandler(e)}
                                        onKeyDown={(e) => adjustCaptionFieldHeight(e)}
                                        onKeyUp={(e) => adjustCaptionFieldHeight(e)}
                                        value={caption}
                                        style={{ fontSize: PublicVariables.isIOSDevice ? "16px" : "" }}
                                    ></textarea>
                                    <span className='for-bg'></span>
                                    <span className='for-border'></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='upload-file-markup-menu' id='upload-file-markup-menu'>
                    <MarkupMenubar fromMarkup={MARKUP_MENU_TYPE.UPLOAD_MARKUP} />
                </div>
                <div className='upload-file-bottom-menu'>
                    <div className='menu-file-list'>
                        <div className='menu-file-slider custom-horizontal-scroll'>
                            {imagesArray.map((img, index) => {
                                return (
                                    img[IMAGE_OBJ_KEYS.UPLOADING_STATUS] === UPLOADING_FILE_STATUS.MARKUP && (
                                        <div
                                            className={clsx(
                                                "upload-menu-thumbnail-box",
                                                img[IMAGE_OBJ_KEYS.ID] === UPLOAD_FILES_DATA.selectedImageObj?.id && "active"
                                            )}
                                            key={"footerImage-" + img[IMAGE_OBJ_KEYS.ID]}
                                        >
                                            <div className='file-box' onClick={() => selectImage(img, false)} onKeyDown={() => {}}>
                                                <img
                                                    className='upload-menu-img'
                                                    src={img[IMAGE_OBJ_KEYS.THUMBNAIL]}
                                                    id={"footerImage-" + img[IMAGE_OBJ_KEYS.ID]}
                                                    alt=''
                                                />
                                            </div>
                                            <div className='delete-file custom-tooltip tooltip-auto-width m-hide'>
                                                <button
                                                    className='btn delete-btn'
                                                    onClick={() => {
                                                        deleteImageFromMarkUp(index);
                                                    }}
                                                >
                                                    <span className='btn-icon icon-close-light'></span>
                                                </button>
                                                <div className='custom-tooltip-content text-nowrap'>
                                                    <div className='custom-tooltip-in'>
                                                        <p>{localisedValues?.delete_lable}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                );
                            })}
                        </div>
                        {showAddButton && !(isOfflineModeActive && disablePlusButton) && (
                            <div className='markup-add-file'>
                                <div className='markup-add-file-box'>
                                    <button className='btn add-file-btn' onClick={() => addImageClickHandler()}>
                                        <span className='for-icon icon-plus'></span>
                                        <input
                                            className='add-file-input d-none'
                                            id='file-upload-from-markup'
                                            title=''
                                            type='file'
                                            accept='image/png,image/jpeg,image/jpg'
                                            multiple
                                            onChange={(e) => dragChange(e)}
                                            onClick={(e) => {
                                                e.target.value = null;
                                            }}
                                        />
                                        <input
                                            className='d-none'
                                            id='cameraFileInput_MarkupFiles'
                                            type='file'
                                            accept='image/*'
                                            capture='camera'
                                            onClick={(e) => {
                                                e.target.value = null;
                                            }}
                                            onChange={(e) => dragChange(e)}
                                        />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className='file-upload-btn'>
                        <div className='button'>
                            <button className='btn btn-primary text-nowrap' onClick={() => uploadClickHandler()}>
                                <span className='btn-text'>{localisedValues?.upload + " (" + getImageCountByStatus() + ")"}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {showCancelUploadModal && (
                <CancelUploadModal onCloseCancelUploadModal={onCloseCancelUploadModal} onConfirmCancelMarkup={onConfirmCancelMarkup} />
            )}
        </>
    );
};

MarkupFiles.propTypes = {
    updatedSelectedFiles: PropTypes.func,
};

export default MarkupFiles;
